import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  AppBar,
  Toolbar,
  Button,
  IconButton,
  useTheme,
  useMediaQuery,
  Grid,
} from "@mui/material";

import { BingoIcon } from "components/BingoIcon";
import BingoAboutPage from "components/BingoAbout";
import WhyBingoComponent from "components/WhyBingo";
import BingoMomentsComponent from "components/bingoMoments";
import BingoStepsComponent from "components/BingoSteps";
import TestimonialsComponent from "components/Testimonials";
import BingoCTASection from "components/Contact";
import Footer from "components/Footer";
import CustomAppBar from "components/CustomHeader";

function Landing() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isTablet = useMediaQuery(theme.breakpoints.down("lg"));
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const heroSectionHeight = window.innerHeight - 64; // Subtract app bar height
      setScrolled(scrollTop > heroSectionHeight * 0.3); // Start transition earlier for smoother effect
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <Box>
      <CustomAppBar />
      <Box
        sx={{
          position: "relative",
          minHeight: "100vh",
          overflow: "hidden",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {/* Background Video */}
        <video
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            zIndex: 0,
          }}
          autoPlay
          loop
          muted
          playsInline
        >
          <source src="./Bingo-Video.mp4" type="video/mp4" />
        </video>

        {/* Modern Gradient Overlay */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background:
              "linear-gradient(135deg, rgba(0, 0, 0, 0.7) 0%, rgba(0, 0, 0, 0.3) 100%)",
            zIndex: 1,
          }}
        />

        {/* Content Container */}
        <Box
          sx={{
            position: "relative",
            zIndex: 10,
            width: "100%",
            paddingTop: "80px", // Account for header
            paddingBottom: "40px",
          }}
        >
          <Container maxWidth="xl">
            <Grid
              container
              spacing={4}
              alignItems="center"
              justifyContent={{ xs: "center", lg: "flex-start" }}
            >
              <Grid item xs={12} lg={7}>
                <Box
                  sx={{
                    textAlign: { xs: "center", lg: "left" },
                    maxWidth: { xs: "800px", lg: "none" },
                    margin: { xs: "0 auto", lg: "0" },
                    px: { xs: 3, md: 4, lg: 6 },
                    ml: { lg: 4 }, // Add left margin on large screens
                  }}
                >
                  {/* Main Title */}
                  <Typography
                    variant="h1"
                    sx={{
                      fontSize: {
                        xs: "3rem",
                        sm: "4rem",
                        md: "5rem",
                        lg: "6rem",
                        xl: "7rem",
                      },
                      fontWeight: 900,
                      color: "white",
                      lineHeight: 0.9,
                      letterSpacing: "-0.02em",
                      mb: 3,
                      textShadow: "0 8px 32px rgba(0, 0, 0, 0.4)",
                      background:
                        "linear-gradient(135deg, #ffffff 0%, #f0f0f0 100%)",
                      backgroundClip: "text",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      position: "relative",
                      "&::after": {
                        content: '""',
                        position: "absolute",
                        bottom: "-10px",
                        left: "50%",
                        transform: "translateX(-50%)",
                        width: "100px",
                        height: "4px",
                        background: "linear-gradient(90deg, #5c26a3, #dc26dc)",
                        borderRadius: "2px",
                        display: { xs: "none", md: "block" },
                      },
                    }}
                  >
                    BINGO SOCIAL
                  </Typography>

                  {/* Subtitle */}
                  <Typography
                    variant="h5"
                    sx={{
                      fontSize: {
                        xs: "1.1rem",
                        sm: "1.3rem",
                        md: "1.5rem",
                        lg: "1.7rem",
                        xl: "1.9rem",
                      },
                      color: "rgba(255, 255, 255, 0.95)",
                      fontWeight: 400,
                      lineHeight: 1.6,
                      mb: 4,
                      letterSpacing: "0.01em",
                      textShadow: "0 4px 16px rgba(0, 0, 0, 0.3)",
                      maxWidth: { xs: "600px", lg: "none" },
                      margin: { xs: "0 auto", lg: "0" },
                      marginBottom: 4,
                      textAlign: { xs: "center", lg: "left" },
                    }}
                  >
                    🎉 Transform every event and workday into an exciting
                    experience with interactive Bingo games
                  </Typography>

                  {/* CTA Button */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: { xs: "center", lg: "flex-start" },
                      gap: 2,
                      flexWrap: "wrap",
                    }}
                  >
                    <Button
                      onClick={() =>
                        window.open(
                          "https://admin-mybingosocial.pages.dev/login",
                          "_self"
                        )
                      }
                      sx={{
                        background:
                          "linear-gradient(135deg, #5c26a3 0%, #dc26dc 100%)",
                        color: "white",
                        fontWeight: 600,
                        fontSize: { xs: "1.1rem", md: "1.2rem" },
                        padding: { xs: "14px 32px", md: "16px 40px" },
                        borderRadius: "50px",
                        textTransform: "none",
                        letterSpacing: "0.02em",
                        boxShadow: "0 8px 32px rgba(92, 38, 163, 0.4)",
                        border: "2px solid transparent",
                        position: "relative",
                        overflow: "hidden",
                        minWidth: "200px",
                        "&::before": {
                          content: '""',
                          position: "absolute",
                          top: 0,
                          left: "-100%",
                          width: "100%",
                          height: "100%",
                          background:
                            "linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)",
                          transition: "left 0.6s ease",
                        },
                        "&:hover": {
                          transform: "translateY(-2px)",
                          background:
                            "linear-gradient(135deg, #6d2bb8 0%, #e842e8 100%)",
                          boxShadow: "0 12px 40px rgba(92, 38, 163, 0.6)",
                          "&::before": {
                            left: "100%",
                          },
                        },
                        "&:active": {
                          transform: "translateY(0)",
                        },
                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                      }}
                      startIcon={
                        <Box
                          sx={{
                            fontSize: { xs: "1.3rem", md: "1.5rem" },
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          🎯
                        </Box>
                      }
                    >
                      Host a Game
                    </Button>
                  </Box>

                  {/* Optional: Feature highlights */}
                  <Box
                    sx={{
                      mt: 6,
                      display: "flex",
                      justifyContent: { xs: "center", lg: "flex-start" },
                      gap: { xs: 2, md: 4 },
                      flexWrap: "wrap",
                      opacity: 0.9,
                    }}
                  >
                    {["Easy Setup", "Real-time Play", "Custom Games"].map(
                      (feature, index) => (
                        <Box
                          key={index}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            color: "rgba(255, 255, 255, 0.8)",
                            fontSize: { xs: "0.9rem", md: "1rem" },
                            fontWeight: 500,
                          }}
                        >
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: "50%",
                              background:
                                "linear-gradient(135deg, #5c26a3, #dc26dc)",
                            }}
                          />
                          {feature}
                        </Box>
                      )
                    )}
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Container>
        </Box>
      </Box>
      <BingoAboutPage />
      <WhyBingoComponent />
      <BingoMomentsComponent />
      <BingoStepsComponent />
      <TestimonialsComponent />
      <BingoCTASection />
      <Footer />

      <style>{`
        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }
      `}</style>
    </Box>
  );
}

export default Landing;
